import styled from "@emotion/styled";
import type { Theme } from "@emotion/react";
import { Button, Card, Chip, Typography } from "@avenue-8/sidekick-ui";
import type { ConfidenceLevel } from "./profile-suggestion-helpers";

export const SuggestionCard = styled(Card)`
  border: 1px solid ${(p) => p.theme.colors.border2};
  box-shadow: none;
`;

export const ContactRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-bottom: 0.75rem;
  color: ${(p) => p.theme.text.colors.secondary};
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;

  ${(p) => p.theme.media.mobileDown} {
    flex-wrap: wrap;
  }
`;

export const Identity = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  line-height: 1.2;
  min-width: 0;
`;

export const ProfileUrl = styled(Typography)`
  overflow-wrap: anywhere;
`;

export const PlatformBadge = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  flex-shrink: 0;
  border-radius: 0.5rem;
  background-color: ${(p) => p.theme.colors.background5};
  color: ${(p) => p.theme.icon.colors.primary};
`;

const CONFIDENCE_TEXT_COLOR: Record<ConfidenceLevel, (theme: Theme) => string> =
  {
    low: (theme) => theme.colors.warning,
    medium: (theme) => theme.text.colors.brand,
    high: (theme) => theme.colors.success,
    veryHigh: (theme) => theme.colors.success,
  };

export const ConfidenceChip = styled(Chip)<{ $level: ConfidenceLevel }>`
  flex-shrink: 0;
  margin-left: auto;
  background-color: ${(p) => p.theme.colors.background5};

  .MuiChip-label {
    font-size: ${(p) => p.theme.text.fontSizes.s};
    font-weight: 600;
    color: ${(p) => CONFIDENCE_TEXT_COLOR[p.$level](p.theme)};
  }
`;

export const FactsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin-top: 0.75rem;
`;

export const MatchedFactChip = styled(Chip)`
  background-color: ${(p) => p.theme.colors.background5};

  .MuiChip-icon {
    font-size: 0.875rem;
    color: ${(p) => p.theme.colors.success};
  }

  .MuiChip-label {
    font-size: ${(p) => p.theme.text.fontSizes.s};
    color: ${(p) => p.theme.text.colors.brand};
  }
`;

export const MismatchedFactChip = styled(Chip)`
  background-color: ${(p) => p.theme.colors.background5};

  .MuiChip-icon {
    font-size: 0.875rem;
    color: ${(p) => p.theme.colors.warning};
  }

  .MuiChip-label {
    font-size: ${(p) => p.theme.text.fontSizes.s};
    color: ${(p) => p.theme.text.colors.secondary};
  }
`;

export const AmbiguityNote = styled(Typography)`
  display: block;
  margin-top: 0.75rem;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  background-color: ${(p) => p.theme.colors.background2};
  color: ${(p) => p.theme.text.colors.secondary};
`;

export const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1rem;

  ${(p) => p.theme.media.mobileDown} {
    & > * {
      flex: 1;
    }
  }
`;

export const CompactButton = styled(Button)`
  padding: 0.375rem 1rem;
  min-width: 0;
  font-size: ${(p) => p.theme.text.fontSizes.s};
`;
